import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, Search, ShoppingBag, Target, User } from 'lucide-react';

export const personalShopper: Playbook = {
  meta: {
    id: 'personal-shopper',
    slug: 'personal-shopper',
    categoryId: 'amplify-marketing',
    name: 'The Personal Shopper',
    tagline: 'Recommend the perfect product.',
    description: 'Act as a personal shopping Genie that asks qualifying questions to understand needs and recommends the ideal product or plan.',
    strategy: 'Act as a personal shopping Genie that asks questions to understand needs and recommends the ideal product or plan.',
    benefits: [
      'Personalized recommendations',
      'Higher conversion rates',
      'Better customer satisfaction',
      'Increased average order value'
    ],
    targetAudience: ['E-commerce stores', 'SaaS platforms', 'Service providers'],
    estimatedSetupTime: '22 minutes',
    difficulty: 'intermediate',
    tags: ['personalization', 'recommendations', 'shopping-genie', 'product-matching'],
    isFeatured: true
  },
  seo: {
    metaTitle: 'Personal Shopper Playbook - Genie Product Recommendations',
    metaDescription: 'Boost sales with personal shopping Genies. Personalized recommendations and consultative selling at scale.',
    keywords: ['personal shopper', 'product recommendations', 'shopping Genie', 'personalized selling', 'consultative sales']
  },
  demoConfig: {
    triggerType: 'button',
    message: "🛍️ I'm your personal shopping Genie! Tell me about your needs, and I'll find the perfect solution for you.",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "I'll help you find the perfect product! What's your main goal?" },
    { speaker: 'user', message: "I need to track my team's productivity" },
    { speaker: 'agent', message: "How large is your team?" },
    { speaker: 'user', message: "About 15 people" },
    { speaker: 'agent', message: "Based on your needs, I recommend our Business plan. It includes team analytics and costs $199/month. Want to see a comparison?" }
  ],
  templateConfig: {
    firstMessage: "🛍️ I'm here to help you find exactly what you need. What problem are you trying to solve?",
    systemPrompt: "You are a personal shopping Genie. Ask qualifying questions to understand customer needs, then recommend the most suitable products or plans. Be consultative and helpful.",
    tools: ['product_catalog', 'recommendation_engine', 'comparison_charts']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Customer needs assistance',
      description: 'Visitor expresses interest in products but seems uncertain about options'
    },
    {
      type: 'genie',
      icon: Bot,
      genieIcon: 'genie-lamp',
      label: 'Initiates consultation',
      description: 'Genie begins consultative conversation to understand specific needs and preferences'
    },
    {
      type: 'action',
      icon: Target,
      genieIcon: 'target',
      label: 'Qualifies requirements',
      description: 'Asks targeted questions about budget, use case, team size, or specific features needed'
    },
    {
      type: 'action',
      icon: Search,
      genieIcon: 'database',
      label: 'Analyzes product catalog',
      description: 'Uses gathered information to identify best-matching products or plans'
    },
    {
      type: 'action',
      icon: ShoppingBag,
      genieIcon: 'target',
      label: 'Presents personalized recommendation',
      description: 'Recommends specific solution with clear explanation of why it\'s the best fit'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Confident purchase decision',
      description: 'Customer feels confident buying the right product for their specific needs'
    }
  ],
  metrics: {
    conversionRate: '41%',
    responseTime: '<2 seconds',
    satisfactionScore: '4.7/5',
    costSavings: '$65k/month',
    timeToValue: '3 minutes'
  },
  testimonials: [
    {
      companyName: 'StyleCraft Boutique',
      authorName: 'Sarah Kim',
      authorTitle: 'Sales Manager',
      quote: 'Personal shopper Genie increased our average order value by 38% and customer satisfaction scores by 25%.',
      metrics: '38% higher average order value'
    }
  ],
  resources: [
    {
      type: 'guide',
      title: 'Product Catalog Integration',
      description: 'How to connect your product database for intelligent recommendations',
      url: '/docs/product-catalog-integration'
    },
    {
      type: 'template',
      title: 'Qualification Question Templates',
      description: 'Proven question flows for different product categories and industries',
      url: '/templates/qualification-questions'
    }
  ]
};